import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Chip, Grid, Collapse, Pagination,
  ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem,
  Accordion, AccordionSummary, AccordionDetails, Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import { searchNoticePapers } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';

const THEME_COLOR = '#d32f2f';  // Red for Senate

export default function SenateNoticePapersSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [facets, setFacets] = useState({});

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    parliamentNumber: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.parliamentNumber) parts.push(`parliament_number:${form.parliamentNumber}`);
    if (form.dateFrom) parts.push(`date:>=${form.dateFrom}`);
    if (form.dateTo) parts.push(`date:<=${form.dateTo}`);

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, year = selectedYear, month = selectedMonth, pageSize = perPage) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    const q = buildQueryWithFilters(baseQuery, filters);

    setLoading(true);
    setError(null);
    try {
      const data = await searchNoticePapers(q, newPage, pageSize, 'Senate', year, month);
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      const newParams = { q: baseQuery, mode: searchMode };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setInitialLoading(false);
    setLoading(false);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, null, null);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const facetToField = (facetName) => {
    const mapping = {
      'parliaments': 'parliament_number'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'parliaments': 'Parliament'
    };
    return names[facetName] || facetName;
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      parliamentNumber: '',
      dateFrom: '',
      dateTo: ''
    });
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSearchParams({});
    handleSearch('*', 1, {}, null, null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ color: THEME_COLOR, fontSize: 32 }} />
          <Typography variant="h4">Senate Notice Papers</Typography>
        </Box>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The Notice Paper is an official document of the Senate listing all business before the Senate
        including notices of motion, orders of the day, questions on notice, and committee reports.
      </Typography>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search Senate Notice Papers..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4, bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#b71c1c' } }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search in notice paper content"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Parliament</InputLabel>
                <Select value={form.parliamentNumber} onChange={handleChange('parliamentNumber')} label="Parliament">
                  <MenuItem value="">All Parliaments</MenuItem>
                  <MenuItem value="48">48th Parliament</MenuItem>
                  <MenuItem value="47">47th Parliament</MenuItem>
                  <MenuItem value="46">46th Parliament</MenuItem>
                  <MenuItem value="45">45th Parliament</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={form.dateFrom}
                onChange={handleChange('dateFrom')}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={form.dateTo}
                onChange={handleChange('dateTo')}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2, bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#b71c1c' } }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Loading skeleton for initial load */}
      {initialLoading && <SearchSkeleton count={5} showFacets={true} />}

      {/* Error state */}
      {!initialLoading && error && (
        <ErrorState
          message={error}
          onRetry={() => handleSearch()}
          themeColor={THEME_COLOR}
        />
      )}

      {/* Empty state when no results */}
      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="notice papers"
          onSuggestionClick={(q) => {
            setQuery(q);
            handleSearch(q);
          }}
          onReset={handleReset}
          themeColor={THEME_COLOR}
        />
      )}

      {/* Results display */}
      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {Object.keys(facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {(Object.keys(activeFilters).length > 0 || selectedYear) && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }}>Clear All</Button>
                  )}
                </Box>

                {/* Date Hierarchy Filter */}
                {(facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0) && (
                  <DateHierarchyFilter
                    facets={facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={selectedDay}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                    themeColor="error"
                  />
                )}

                {['parliaments'].map((facetName) => (
                  facets[facetName] && facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={true}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetDisplayName(facetName)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {facets[facetName].slice(0, 15).map((facet) => (
                            <ListItem key={facet.value} disablePadding>
                              <Checkbox
                                size="small"
                                checked={activeFilters[facetToField(facetName)]?.includes(String(facet.value)) || false}
                                onChange={() => handleFacetClick(facetToField(facetName), String(facet.value))}
                              />
                              <ListItemText
                                primary={`Parliament ${facet.value} (${facet.count.toLocaleString()})`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          {/* Results */}
          <Grid item xs={12} md={Object.keys(facets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1">
                    {results.total.toLocaleString()} notice paper{results.total !== 1 ? 's' : ''} found
                  </Typography>
                  <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                </Box>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography
                              variant="resultTitle"
                              component={RouterLink}
                              to={`/senate-notice-papers/${encodeURIComponent(hit.id)}`}
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {hit.title || 'Notice Paper'}
                            </Typography>
                            <Chip
                              label="Senate"
                              size="small"
                              sx={{ bgcolor: '#ffebee', color: THEME_COLOR }}
                            />
                            {hit.parliament_number && (
                              <Chip
                                label={`Parliament ${hit.parliament_number}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {formatDate(hit.date)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
